import { BookOpenText, Languages, PenSquare } from "lucide-react";
import { Link, useLocation } from "react-router";
import { Badge } from "~/components/ui/badge";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "~/components/ui/sidebar";

type SidebarIcon = typeof PenSquare;

export interface AppSidebarItem {
  title: string;
  to: string;
  icon: SidebarIcon;
}

const sidebarItems: AppSidebarItem[] = [
  {
    title: "Editor",
    to: "/",
    icon: PenSquare,
  },
  {
    title: "Classic",
    to: "/classic",
    icon: BookOpenText,
  },
  {
    title: "Phonetic Resolver",
    to: "/phonetic-resolver",
    icon: Languages,
  },
];

function isPathActive(currentPath: string, to: string) {
  if (to === "/") return currentPath === "/";
  return currentPath.startsWith(to);
}

export function AppSidebar() {
  const { pathname } = useLocation();

  return (
    <Sidebar collapsible="icon" data-testid="app-sidebar" variant="sidebar">
      <SidebarHeader>
        <Link
          className="rounded-lg border border-sidebar-border bg-sidebar px-3 py-2 text-sm font-semibold text-sidebar-foreground"
          to="/"
        >
          Fluentry
        </Link>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Workspace</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {sidebarItems.map((item) => {
                const active = isPathActive(pathname, item.to);
                const Icon = item.icon;
                return (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton asChild isActive={active} tooltip={item.title}>
                      <Link to={item.to}>
                        <Icon />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <div className="rounded-lg border border-sidebar-border bg-sidebar-accent/40 p-3 text-xs">
          <p className="font-medium text-sidebar-foreground">Learning Mode</p>
          <p className="mt-1 text-sidebar-foreground/80">Keep writing and get instant guidance.</p>
          <Badge variant="secondary" className="mt-2 bg-sidebar-accent text-sidebar-accent-foreground">
            AI Assisted
          </Badge>
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
